import React, { FormEvent, useEffect, useRef, useState, memo } from 'react'
import cn, { Argument as ClassName } from 'classnames'
import { useDispatch, useSelector } from 'react-redux'
import { Button } from 'primereact/button'
import { useLocation, useNavigate } from 'react-router-dom'

import { theme } from '../../../lib/theme'
import { LandingPageLayout } from '../../../lib/landing-page-layout'
import { useSignOut } from '../../../lib/sign-out'
import {
    AuthenticationServiceContextRequest,
    useCompanyGetAdministratedCompaniesQuery,
    useEntitlementsCreateContextTokenMutation,
} from '../../../common/redux/api/web-api'
import { useApplyOrganisationSelection } from '../../../common/hooks/useApplyOrganisationSelection'
import {
    clearEntitlements,
    normalizeEntitlementsState,
    setEntitlements,
} from '../../../common/redux/features/entitlements/entitlementsSlice'
import { selectIsConfirmedScopedAccessActive } from '../../redux/features/scopedAccessSession/scopedAccessSessionSlice'
import { AppDispatch, RootState } from '../../redux/store'
import useText from '../../../common/hooks/useText'
import { FormHeader } from '../../../lib/form-header'
import { Dropdown } from '../../../common/uikit/Dropdown'
import { useToast } from '../../../common/uikit/ToastProvider'

export type SelectOrganisationPageProps = {
    className?: ClassName
    style?: React.CSSProperties
}

export const SelectOrganisationPage = memo(function SelectOrganisationPage({
    className,
    style,
}: SelectOrganisationPageProps): React.ReactElement {
    const text = useText()
    const navigate = useNavigate()
    const toast = useToast()
    const signOut = useSignOut()
    const applyOrganisationSelection = useApplyOrganisationSelection()

    const location = useLocation()
    const from = location?.state?.from || '/'

    const [selectedOrganisationId, setSelectedOrganisationId] = useState<string | null>(null)
    const [isEntitlementsLoaded, setIsEntitlementsLoaded] = useState(false)
    const [isFetchingEntitlements, setIsFetchingEntitlements] = useState(false)
    const latestEntitlementsRequest = useRef(0)
    const { data: organisations, isLoading } = useCompanyGetAdministratedCompaniesQuery()
    const [getEntitlements] = useEntitlementsCreateContextTokenMutation()

    const dispatch = useDispatch<AppDispatch>()
    const signedInUser = useSelector((state: RootState) => state.user)
    const isConfirmedScopedAccessActive = useSelector(selectIsConfirmedScopedAccessActive)

    useEffect(() => {
        // handle logged in scenario
        if (signedInUser?.selectedCompanyId) {
            navigate(from || '/dashboard', { replace: true })
        }
    }, [from, navigate, signedInUser?.selectedCompanyId])

    const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault()

        if (!signedInUser || !selectedOrganisationId) {
            navigate('/login', { replace: true })
            return
        }

        try {
            await applyOrganisationSelection({
                organizationId: selectedOrganisationId,
            })
            navigate(from || '/dashboard', { replace: true })
        } catch {
            dispatch(clearEntitlements())
            toast.notifyError(text('lblCouldNotGetEntitlements'))
        }
    }

    const organisationOptions = organisations?.map((organisation) => ({
        label: organisation?.name,
        value: organisation.id,
    }))

    const handleSelectedOrg = async (selectedOrgId: string | null) => {
        const requestId = latestEntitlementsRequest.current + 1
        latestEntitlementsRequest.current = requestId
        setSelectedOrganisationId(selectedOrgId)
        setIsEntitlementsLoaded(false)

        if (!selectedOrgId) {
            dispatch(clearEntitlements())
            setIsFetchingEntitlements(false)
            return
        }

        setIsFetchingEntitlements(true)

        try {
            const authenticationServiceContextRequest: AuthenticationServiceContextRequest = {
                organizationId: selectedOrgId,
            }

            const res = await getEntitlements({ authenticationServiceContextRequest }).unwrap()

            if (requestId !== latestEntitlementsRequest.current) {
                return
            }

            dispatch(setEntitlements(normalizeEntitlementsState(res, selectedOrgId)))
            setIsEntitlementsLoaded(true)
        } catch (error) {
            if (requestId !== latestEntitlementsRequest.current) {
                return
            }

            dispatch(clearEntitlements())
            toast.notifyError(text('lblCouldNotGetEntitlements'))
        } finally {
            if (requestId === latestEntitlementsRequest.current) {
                setIsFetchingEntitlements(false)
            }
        }
    }

    return (
        <LandingPageLayout
            className={cn(className)}
            style={style}
            background={theme.whiteBackground}
            hideLanguageSelect={true}
        >
            <div className="sm:w-8 md:w-6 w-11">
                <FormHeader className="mb-8" />
                <form onSubmit={handleSubmit} noValidate>
                    <Dropdown
                        id="select-organisation"
                        className="w-full"
                        options={organisationOptions}
                        value={selectedOrganisationId}
                        onChange={(option) => handleSelectedOrg(option.value)}
                        loading={isLoading}
                        disabled={isConfirmedScopedAccessActive || isLoading}
                        disabledReason={
                            isConfirmedScopedAccessActive
                                ? text('msgScopedAccessOrganisationSwitchDisabled')
                                : undefined
                        }
                        filter
                        showClear={false}
                        placeholder={text('lblSelectOrganisation')}
                    />
                    <div className="flex flex-column gap-4 justify-content-center align-items-center">
                        <Button
                            disabled={
                                !selectedOrganisationId ||
                                isLoading ||
                                isFetchingEntitlements ||
                                !isEntitlementsLoaded ||
                                isConfirmedScopedAccessActive
                            }
                            type="submit"
                            className="mt-5"
                            label={text('btnNext')}
                            iconPos="right"
                            icon="pi pi-arrow-right"
                        ></Button>
                        <Button
                            link
                            label={text('btnSignOut')}
                            data-testid="btn-logout"
                            onClick={signOut}
                            disabled={isConfirmedScopedAccessActive}
                        ></Button>
                        {isConfirmedScopedAccessActive ? (
                            <small data-testid="select-organisation-logout-disabled-reason">
                                {text('msgScopedAccessLogoutDisabled')}
                            </small>
                        ) : null}
                    </div>
                </form>
            </div>
        </LandingPageLayout>
    )
})
